import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../App.css';
import '../style.css';
import '../font.css';
import waiting from '../image/waiting.webp';

export type PasswordModalVariant = 'page' | 'section';

interface PasswordModalProps {
  isOpen: boolean;
  onSuccess: () => void;
  correctPassword: string | string[];
  verifyPassword?: (inputPassword: string) => boolean;
  description?: string;
  /** 지정 시 «돌아가기»와 배경 탭이 페이지 이동 대신 이 콜백만 호출됩니다. (섹션 잠금 등) */
  onDismiss?: () => void;
  /** 기본: 페이지를 보려면… */
  modalTitle?: React.ReactNode;
  /**
   * `page`: 전체 페이지 잠금 — 패널에 테두리·그림자 없음, 닫기 문구는 «돌아가기»
   * `section`: 섹션 잠금 — 테두리·그림자 유지, 닫기 문구는 «닫기»
   */
  variant?: PasswordModalVariant;
}

const PasswordModal: React.FC<PasswordModalProps> = ({ 
  isOpen, 
  onSuccess, 
  correctPassword,
  verifyPassword,
  description = "이 페이지는 비밀번호로 잠겨있어요",
  onDismiss,
  modalTitle,
  variant = 'section',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 500); // 트렌지션 시간과 동일
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // 암호화된 비밀번호 검증 또는 일반 검증
    const isValid = verifyPassword 
      ? verifyPassword(password)
      : Array.isArray(correctPassword) 
        ? correctPassword.includes(password)
        : password === correctPassword;
    
    if (isValid) {
      setError('');
      setHasError(false);
      onSuccess();
    } else {
      setError('비밀번호가 일치하지 않아요.');
      setPassword(''); // input 값 지우기
      setHasError(true);
    }
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    setHasError(false);

    if (onDismiss) {
      onDismiss();
      return;
    }

    // 브라우저 히스토리가 있는지 확인
    if (window.history.length > 1) {
      navigate(-1); // 이전 페이지로 이동
    } else {
      // 현재 경로에 따라 다른 fallback 페이지로 이동
      const currentPath = location.pathname;
      
      if (currentPath.includes('/Article') || currentPath.includes('/creative')) {
        navigate('/activities'); // article이나 creative 페이지에서 /activities로 이동
      } else if (currentPath.includes('/Work')) {
        navigate('/work'); // work 관련 페이지에서 /work로 이동
      } else {
        navigate('/work'); // 기본적으로 /work로 이동
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (hasError) {
      setHasError(false);
      setError('');
    }
  };

  if (!isVisible) return null;

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onDismiss) return;
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const titleNode =
    modalTitle ?? (
      <>
        페이지를 보려면,
        <br />
        알맞은 답을 입력해주세요
      </>
    );

  return (
    <div
      className={`password-modal-overlay password-modal-overlay--${variant} ${isOpen ? 'modal-open' : 'modal-closing'}`}
      onMouseDown={handleOverlayMouseDown}
      data-theme="light"
    >
      <div
        className={`password-modal-container password-modal-container--${variant} ${isOpen ? 'modal-open' : 'modal-closing'}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <img src={waiting} className="flow" alt="" />
        <div className="password-modal-title">{titleNode}</div>
        <div className="password-modal-description">{description}</div>
        
        <form onSubmit={handleSubmit} className="password-modal-form">
          <div>
            <input
              type="password"
              value={password}
              onChange={handleInputChange}
              placeholder="여기에 알맞은 답을 입력해 주세요"
              className={`password-modal-input ${hasError ? 'password-modal-input-error' : ''}`}
              autoFocus
            />
          </div>
          
          {error && (
            <div className="password-modal-error-message">{error}</div>
          )}
          
          <div className="password-modal-buttons">
            <button
              type="button"
              onClick={handleClose}
              className="password-modal-button password-modal-button-secondary"
            >
              <span>{variant === 'page' ? '돌아가기' : '닫기'}</span>
            </button>
            <button
              type="submit"
              disabled={!password}
              className={`password-modal-button password-modal-button-primary ${password ? 'password-modal-button-active' : ''}`}
            >
              <span>확인</span>
              <svg
                className="confirm-arrow"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14m-7-7l7 7-7 7" />
              </svg>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordModal; 